import { useState, useEffect, useCallback } from "react";

// ── CONFIG ────────────────────────────────────────────────────
const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const WA_PREFIX         = "https://wa.me/";
const SITE_URL          = process.env.REACT_APP_SITE_URL || window.location.origin;
const ADMIN_PASS        = process.env.REACT_APP_ADMIN_PASS || "admin123";

async function sbGet(path) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
  });
  if (!r.ok) return [];
  return await r.json();
}

async function sbPatch(path, body) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method: "PATCH",
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      "Content-Type": "application/json",
      Prefer: "return=representation",
    },
    body: JSON.stringify(body),
  });
  if (!r.ok) return null;
  const d = await r.json();
  return Array.isArray(d) ? d[0] : d;
}

async function sbPostWithError(path, body) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method: "POST",
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      "Content-Type": "application/json",
      Prefer: "return=representation",
    },
    body: JSON.stringify(body),
  });
  const text = await r.text();
  if (!r.ok) {
    console.error(`[sbPost] ${path} → ${r.status}:`, text);
    let msg = `HTTP ${r.status}`;
    try { msg = JSON.parse(text).message || JSON.parse(text).hint || msg; } catch {}
    return { ok: false, error: msg };
  }
  try {
    const d = JSON.parse(text);
    return { ok: true, data: Array.isArray(d) ? d[0] : d };
  } catch {
    return { ok: true, data: null };
  }
}

// ── HELPERS ───────────────────────────────────────────────────
function fmt(n) { return "Rp " + (n||0).toLocaleString("id-ID"); }

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso);
  const m = Math.floor(diff/60000);
  if (m < 1)  return "baru saja";
  if (m < 60) return `${m} menit lalu`;
  const h = Math.floor(m/60);
  if (h < 24) return `${h} jam lalu`;
  return `${Math.floor(h/24)} hari lalu`;
}

function slugify(groom, bride) {
  const clean = s => s.toLowerCase()
    .replace(/[^a-z0-9\s]/g,"").replace(/\s+/g,"-").slice(0,14);
  return `${clean(groom)}-${clean(bride)}-${Date.now().toString(36).slice(-4)}`;
}

function buildWaLink(order) {
  if (!order?.contact_phone) return null;
  const phone = order.contact_phone.replace(/^0/,"62").replace(/\D/g,"");
  const url   = `${SITE_URL}/undangan/${order.slug}`;
  const msg   = encodeURIComponent(
    `Halo ${order.groom_name}! 🎉\n\n` +
    `Undangan digital MyKisah kamu sudah siap!\n\n` +
    `✦ Link Undangan:\n${url}\n\n` +
    `Kamu bisa cek status kapan saja di:\n${SITE_URL}/cek-order\n` +
    `Nomor order: *${order.order_number}*\n\n` +
    `Selamat menempuh hidup baru! 💍`
  );
  return `${WA_PREFIX}${phone}?text=${msg}`;
}

const STATUS_OPTS = [
  { value:"pending_payment",   label:"Menunggu Bayar",    color:"rgba(201,168,76,.7)"  },
  { value:"payment_uploaded",  label:"Bukti Masuk",       color:"#9B87B8"              },
  { value:"payment_confirmed", label:"Bayar Dikonfirmasi",color:"#7A9E7E"              },
  { value:"in_progress",       label:"Dikerjakan",        color:"#C97B6F"              },
  { value:"active",            label:"Aktif",             color:"#C9A84C"              },
  { value:"revision",          label:"Revisi",            color:"rgba(201,123,111,.8)" },
  { value:"expired",           label:"Expired",           color:"rgba(240,232,216,.3)" },
];

function statusMeta(s) {
  return STATUS_OPTS.find(o=>o.value===s) || { label:s, color:"rgba(240,232,216,.3)" };
}

const ADDON_LABELS = {
  link_personal:"🔗 Link Personal", pantau_rsvp:"📊 Pantau RSVP",
  notif_email:"📧 Notif Email",     galeri_foto:"🖼️ Galeri Foto",
  amplop_digital:"💌 Amplop",       live_streaming:"📺 Live Stream",
  musik_latar:"🎵 Musik Latar",     custom_subdomain:"🌐 Subdomain",  selamanya:"♾️ Selamanya",
  qr_checkin:"✅ QR Check-in",      export_excel:"📥 Export Excel",
};

// ── CSS ───────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500&family=Josefin+Sans:wght@200;300;400&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  html { scroll-behavior:smooth;  font-size:16px; }
  body { background:#0A0907; color:rgba(240,232,216,.88); font-family:'Josefin Sans',sans-serif; font-weight:300; min-height:100vh; }
  @keyframes adFadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes adSpin   { to{transform:rotate(360deg)} }

  /* ── LAYOUT ── */
  .ad-layout { display:grid; grid-template-columns:220px 1fr; min-height:100vh; }

  /* ── SIDEBAR NAV ── */
  .ad-nav { background:#070605; border-right:1px solid rgba(201,168,76,.08); padding:28px 0; display:flex; flex-direction:column; position:sticky; top:0; height:100vh; overflow-y:auto; }
  .ad-nav-logo { font-family:'Cinzel',serif; font-size:14px; letter-spacing:.38em; color:rgba(201,168,76,.55); padding:0 22px 28px; border-bottom:1px solid rgba(201,168,76,.07); }
  .ad-nav-logo span { display:block; font-size:11px; letter-spacing:.22em; color:rgba(240,232,216,.2); margin-top:3px; }
  .ad-nav-section { font-family:'Cinzel',serif; font-size:11px; letter-spacing:.44em; color:rgba(201,168,76,.3); padding:22px 22px 8px; }
  .ad-nav-item { display:flex; align-items:center; gap:10px; padding:10px 22px; cursor:pointer; transition:background .2s; font-size:13px; letter-spacing:.12em; color:rgba(240,232,216,.45); border-left:2px solid transparent; }
  .ad-nav-item:hover  { background:rgba(201,168,76,.04); color:rgba(240,232,216,.75); }
  .ad-nav-item.active { background:rgba(201,168,76,.07); color:rgba(240,232,216,.9); border-left-color:rgba(201,168,76,.6); }
  .ad-nav-icon { font-size:16px; width:18px; text-align:center; }
  .ad-nav-badge { margin-left:auto; font-family:'Cinzel',serif; font-size:10px; letter-spacing:.1em; background:rgba(201,168,76,.18); color:rgba(201,168,76,.8); padding:1px 6px; border-radius:10px; }

  /* ── MAIN CONTENT ── */
  .ad-main { overflow-x:auto; }
  .ad-topbar { padding:16px 28px; border-bottom:1px solid rgba(201,168,76,.07); display:flex; justify-content:space-between; align-items:center; gap:14px; flex-wrap:wrap; position:sticky; top:0; background:#0A0907; z-index:10; }
  .ad-topbar-title { font-family:'Cinzel',serif; font-size:15px; letter-spacing:.22em; color:rgba(240,232,216,.8); }
  .ad-topbar-right { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
  .ad-refresh { padding:7px 14px; background:transparent; border:1px solid rgba(201,168,76,.18); color:rgba(201,168,76,.6); font-family:'Cinzel',serif; font-size:12px; letter-spacing:.18em; cursor:pointer; transition:all .2s; }
  .ad-refresh:hover { background:rgba(201,168,76,.06); }
  .ad-content { padding:28px; }

  /* ── STATS ── */
  .ad-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:10px; margin-bottom:28px; }
  .ad-stat { border:1px solid rgba(201,168,76,.1); padding:16px 18px; }
  .ad-stat-label { font-family:'Cinzel',serif; font-size:11px; letter-spacing:.38em; color:rgba(201,168,76,.44); margin-bottom:8px; }
  .ad-stat-val   { font-family:'Cinzel',serif; font-size:clamp(1.4rem,3vw,2rem); color:rgba(240,232,216,.9); line-height:1; }
  .ad-stat-sub   { font-size:11px; letter-spacing:.1em; color:rgba(240,232,216,.28); margin-top:5px; }

  /* ── FILTERS ── */
  .ad-filters { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:18px; }
  .ad-filter { padding:6px 14px; background:transparent; border:1px solid rgba(201,168,76,.12); color:rgba(240,232,216,.4); font-family:'Cinzel',serif; font-size:12px; letter-spacing:.16em; cursor:pointer; transition:all .2s; }
  .ad-filter:hover  { border-color:rgba(201,168,76,.3); color:rgba(240,232,216,.7); }
  .ad-filter.active { border-color:rgba(201,168,76,.55); color:rgba(201,168,76,.85); background:rgba(201,168,76,.06); }
  .ad-search-inp { padding:7px 12px; background:rgba(255,255,255,.02); border:1px solid rgba(201,168,76,.12); color:rgba(240,232,216,.8); font-family:'Josefin Sans',sans-serif; font-size:17px; font-weight:300; outline:none; width:220px; }
  .ad-search-inp:focus { border-color:rgba(201,168,76,.3); }
  .ad-search-inp::placeholder { color:rgba(240,232,216,.2); }

  /* ── TABLE ── */
  .ad-table-wrap { overflow-x:auto; border:1px solid rgba(201,168,76,.09); }
  table { width:100%; border-collapse:collapse; min-width:780px; }
  thead tr { border-bottom:1px solid rgba(201,168,76,.12); background:rgba(201,168,76,.04); }
  th { font-family:'Cinzel',serif; font-size:11px; letter-spacing:.28em; color:rgba(201,168,76,.52); padding:11px 14px; text-align:left; white-space:nowrap; }
  tbody tr { border-bottom:1px solid rgba(255,255,255,.04); transition:background .15s; cursor:pointer; }
  tbody tr:hover { background:rgba(201,168,76,.03); }
  td { padding:12px 14px; font-size:13px; letter-spacing:.08em; color:rgba(240,232,216,.65); vertical-align:middle; }
  .td-couple { color:rgba(240,232,216,.88); font-size:13px; }
  .td-bride  { color:rgba(201,168,76,.8); }
  .td-order  { font-family:'Cinzel',serif; font-size:12px; letter-spacing:.16em; color:rgba(201,168,76,.55); }
  .td-badge  { display:inline-block; padding:2px 9px; border-radius:2px; font-family:'Cinzel',serif; font-size:10px; letter-spacing:.14em; border:1px solid; }
  .td-price  { font-family:'Cinzel',serif; font-size:13px; color:rgba(240,232,216,.75); }
  .td-time   { font-size:12px; color:rgba(240,232,216,.3); white-space:nowrap; }

  /* ── ORDER DETAIL DRAWER ── */
  .ad-overlay { position:fixed; inset:0; background:rgba(0,0,0,.65); z-index:100; backdrop-filter:blur(3px); }
  .ad-drawer  { position:fixed; top:0; right:0; bottom:0; width:min(520px,100vw); background:#0D0B09; border-left:1px solid rgba(201,168,76,.12); z-index:101; overflow-y:auto; animation:adFadeUp .3s ease both; display:flex; flex-direction:column; }
  .ad-drawer-head { padding:20px 24px; border-bottom:1px solid rgba(201,168,76,.08); display:flex; justify-content:space-between; align-items:flex-start; gap:12px; position:sticky; top:0; background:#0D0B09; z-index:2; }
  .ad-drawer-title { font-family:'Cinzel',serif; font-size:15px; letter-spacing:.2em; color:rgba(240,232,216,.85); }
  .ad-drawer-close { background:transparent; border:none; font-size:1rem; color:rgba(240,232,216,.3); cursor:pointer; padding:4px; transition:color .2s; line-height:1; }
  .ad-drawer-close:hover { color:rgba(240,232,216,.7); }
  .ad-drawer-body { padding:22px 24px; flex:1; }
  .ad-drawer-section { margin-bottom:24px; }
  .ad-drawer-sec-title { font-family:'Cinzel',serif; font-size:12px; letter-spacing:.38em; color:rgba(201,168,76,.44); margin-bottom:12px; }
  .ad-drow { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
  .ad-drow-item { }
  .ad-drow-key { font-size:11px; letter-spacing:.26em; color:rgba(240,232,216,.3); margin-bottom:3px; }
  .ad-drow-val { font-size:13px; letter-spacing:.08em; color:rgba(240,232,216,.75); word-break:break-all; }
  .ad-chips { display:flex; flex-wrap:wrap; gap:5px; }
  .ad-chip  { font-size:11px; letter-spacing:.08em; padding:3px 8px; background:rgba(201,168,76,.06); border:1px solid rgba(201,168,76,.15); color:rgba(240,232,216,.5); }

  /* drawer actions */
  .ad-drawer-actions { padding:18px 24px; border-top:1px solid rgba(201,168,76,.08); display:flex; flex-direction:column; gap:8px; position:sticky; bottom:0; background:#0D0B09; }
  .ad-act-row { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
  .ad-btn-primary { padding:11px; background:#C9A84C; color:#0A0907; font-family:'Cinzel',serif; font-size:13px; letter-spacing:.22em; border:none; cursor:pointer; transition:opacity .2s; }
  .ad-btn-primary:hover { opacity:.85; }
  .ad-btn-primary:disabled { opacity:.35; cursor:default; }
  .ad-btn-ghost { padding:10px; background:transparent; border:1px solid rgba(201,168,76,.18); color:rgba(201,168,76,.7); font-family:'Cinzel',serif; font-size:13px; letter-spacing:.2em; cursor:pointer; transition:all .2s; }
  .ad-btn-ghost:hover { background:rgba(201,168,76,.06); }
  .ad-btn-danger { padding:10px; background:transparent; border:1px solid rgba(201,123,111,.25); color:rgba(201,123,111,.7); font-family:'Cinzel',serif; font-size:13px; letter-spacing:.2em; cursor:pointer; transition:all .2s; }
  .ad-btn-danger:hover { background:rgba(201,123,111,.06); }
  .ad-btn-wa { padding:11px; background:#25D366; color:#fff; font-family:'Cinzel',serif; font-size:13px; letter-spacing:.22em; border:none; cursor:pointer; transition:opacity .2s; display:flex; align-items:center; justify-content:center; gap:8px; }
  .ad-btn-wa:hover { opacity:.88; }
  .ad-btn-wa:disabled { opacity:.35; cursor:default; }

  /* status select */
  .ad-status-sel { width:100%; padding:9px 12px; background:rgba(255,255,255,.03); border:1px solid rgba(201,168,76,.18); color:rgba(240,232,216,.8); font-family:'Josefin Sans',sans-serif; font-size:15px; font-weight:300; outline:none; appearance:none; cursor:pointer; }

  /* slug input */
  .ad-slug-row { display:flex; gap:0; border:1px solid rgba(201,168,76,.18); }
  .ad-slug-prefix { padding:9px 10px; background:rgba(201,168,76,.06); font-family:'Cinzel',serif; font-size:12px; letter-spacing:.1em; color:rgba(201,168,76,.5); white-space:nowrap; border-right:1px solid rgba(201,168,76,.14); }
  .ad-slug-inp { flex:1; padding:9px 10px; background:rgba(255,255,255,.02); border:none; color:rgba(240,232,216,.8); font-family:'Josefin Sans',sans-serif; font-size:17px; font-weight:300; outline:none; }

  /* content editor */
  .ad-ce-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
  .ad-ce-full  { grid-column:span 2; }
  .ad-ce-field { display:flex; flex-direction:column; gap:4px; }
  .ad-ce-label { font-size:10px; letter-spacing:.26em; color:rgba(201,168,76,.38); }
  .ad-ce-inp   { padding:8px 10px; background:rgba(255,255,255,.02); border:1px solid rgba(201,168,76,.14); color:rgba(240,232,216,.8); font-family:'Josefin Sans',sans-serif; font-size:17px; font-weight:300; outline:none; transition:border-color .2s; }
  .ad-ce-inp:focus { border-color:rgba(201,168,76,.38); }
  .ad-ce-inp::placeholder { color:rgba(240,232,216,.18); }
  .ad-ce-ta    { resize:vertical; min-height:72px; }

  /* gallery preview */
  .ad-gal-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:5px; margin-bottom:8px; }
  .ad-gal-thumb { aspect-ratio:4/3; overflow:hidden; border:1px solid rgba(201,168,76,.1); }
  .ad-gal-thumb img { width:100%; height:100%; object-fit:cover; }
  .ad-gal-empty { aspect-ratio:4/3; border:1px dashed rgba(201,168,76,.08); display:flex; align-items:center; justify-content:center; font-size:10px; color:rgba(201,168,76,.2); }

  /* guest list */
  .ad-guest-row { display:flex; align-items:center; gap:8px; padding:8px 10px; border-bottom:1px solid rgba(201,168,76,.06); font-size:12px; letter-spacing:.06em; }
  .ad-guest-row:last-child { border-bottom:none; }
  .ad-guest-name  { flex:1; color:rgba(240,232,216,.78); }
  .ad-guest-phone { color:rgba(240,232,216,.3); font-size:11px; }
  .ad-guest-token { font-family:monospace; font-size:11px; color:rgba(201,168,76,.4); }
  .ad-guest-empty { padding:16px; text-align:center; font-size:12px; letter-spacing:.14em; color:rgba(240,232,216,.2); }

  /* proof image */
  .ad-proof-img { width:100%; max-height:200px; object-fit:contain; border:1px solid rgba(201,168,76,.14); cursor:pointer; }

  /* toast */
  .ad-toast { position:fixed; bottom:24px; left:50%; transform:translateX(-50%); background:#1A1610; border:1px solid rgba(201,168,76,.3); padding:10px 22px; font-family:'Cinzel',serif; font-size:13px; letter-spacing:.18em; color:rgba(201,168,76,.8); z-index:200; animation:adFadeUp .3s ease both; white-space:nowrap; }

  /* login */
  .ad-login { min-height:100vh; display:flex; align-items:center; justify-content:center; padding:40px 24px; }
  .ad-login-box { width:100%; max-width:360px; border:1px solid rgba(201,168,76,.12); padding:36px 28px; text-align:center; }
  .ad-login-title { font-family:'Cinzel',serif; font-size:1.4rem; letter-spacing:.06em; margin-bottom:6px; }
  .ad-login-sub   { font-size:13px; letter-spacing:.12em; color:rgba(240,232,216,.28); margin-bottom:28px; }
  .ad-login-inp   { width:100%; padding:11px 12px; background:rgba(255,255,255,.02); border:1px solid rgba(201,168,76,.12); color:rgba(240,232,216,.88); font-family:'Josefin Sans',sans-serif; font-size:15px; font-weight:300; outline:none; margin-bottom:10px; text-align:center; letter-spacing:.22em; }
  .ad-login-inp:focus { border-color:rgba(201,168,76,.38); }
  .ad-login-btn { width:100%; padding:12px; background:#C9A84C; color:#0A0907; font-family:'Cinzel',serif; font-size:14px; letter-spacing:.26em; border:none; cursor:pointer; transition:opacity .2s; }
  .ad-login-btn:hover { opacity:.88; }
  .ad-login-err { font-size:12px; color:rgba(201,123,111,.7); margin-top:8px; }

  /* empty */
  .ad-empty { padding:52px; text-align:center; font-family:'Cinzel',serif; font-size:14px; letter-spacing:.26em; color:rgba(240,232,216,.18); }

  /* spin */
  .ad-spin { width:18px; height:18px; border:1px solid rgba(201,168,76,.25); border-top-color:#C9A84C; border-radius:50%; animation:adSpin 1s linear infinite; display:inline-block; }

  @media(max-width:768px) {
    .ad-layout { grid-template-columns:1fr; }
    .ad-nav { position:static; height:auto; flex-direction:row; overflow-x:auto; padding:0; border-right:none; border-bottom:1px solid rgba(201,168,76,.08); }
    .ad-nav-logo { display:none; }
    .ad-nav-section { display:none; }
    .ad-nav-item { padding:14px 16px; border-left:none; border-bottom:2px solid transparent; white-space:nowrap; }
    .ad-nav-item.active { border-left:none; border-bottom-color:rgba(201,168,76,.6); }
    .ad-stats { grid-template-columns:1fr 1fr; }
    .ad-content { padding:18px; }
  }
`;

// ════════════════════════════════════════════════════════════
// MAIN ADMIN
// ════════════════════════════════════════════════════════════
export default function Admin() {
  const [authed,    setAuthed]   = useState(!!sessionStorage.getItem("mk_admin"));
  const [pass,      setPass]     = useState("");
  const [passErr,   setPassErr]  = useState("");
  const [tab,       setTab]      = useState("orders");
  const [orders,    setOrders]   = useState([]);
  const [loading,   setLoading]  = useState(false);
  const [filter,    setFilter]   = useState("all");
  const [search,    setSearch]   = useState("");
  const [selected,  setSelected] = useState(null);
  const [toast,     setToast]    = useState("");
  const [saving,    setSaving]   = useState(false);

  // Drawer edit state
  const [editSlug,    setEditSlug]   = useState("");
  const [editStatus,  setEditStatus] = useState("");
  const [editContent, setEditContent]= useState({
    akad_date:"", akad_time:"", resepsi_date:"", resepsi_time:"",
    venue_name:"", venue_address:"", venue_maps_url:"",
    dress_code:"", target_date:"", love_story:"",
    opening_text:"", music_url:"", live_streaming_url:"",
    gallery: [],    // ← foto dari order
  });
  const [contentSaving,  setContentSaving]  = useState(false);
  const [weddingRecord,  setWeddingRecord]  = useState(null);  // ← untuk panel tamu
  const [guestList,      setGuestList]      = useState([]);
  const [guestsLoading,  setGuestsLoading]  = useState(false);

  // ── Login ──
  function login() {
    if (pass === ADMIN_PASS) {
      sessionStorage.setItem("mk_admin","1");
      setAuthed(true);
    } else {
      setPassErr("Password salah.");
    }
  }

  // ── Fetch orders ──
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    const data = await sbGet("orders?order=created_at.desc&limit=200");
    setOrders(Array.isArray(data) ? data : []);
    setLoading(false);
  }, []);

  useEffect(() => { if (authed) fetchOrders(); }, [authed, fetchOrders]);

  // ── Toast ──
  function showToast(msg) {
    setToast(msg);
    setTimeout(()=>setToast(""),2800);
  }

  // ── Open drawer ──
  async function openOrder(o) {
    setSelected(o);
    setEditSlug(o.slug || slugify(o.groom_name||"", o.bride_name||""));
    setEditStatus(o.status || "pending_payment");
    setWeddingRecord(null);
    setGuestList([]);

    const orderContent = o.content || {};

    // Load wedding record
    const wList = await sbGet(`weddings?order_number=eq.${o.order_number}&limit=1`);
    const w = Array.isArray(wList) && wList.length > 0 ? wList[0] : null;
    setWeddingRecord(w);
    const wContent = w?.content || {};

    // Prioritas: wedding.content (sudah diedit) > order.content (input user)
    const c = Object.keys(wContent).length > 0 ? wContent : orderContent;
    setEditContent({
      akad_date:      c.akad_date      || "",
      akad_time:      c.akad_time      || "",
      resepsi_date:   c.resepsi_date   || "",
      resepsi_time:   c.resepsi_time   || "",
      venue_name:     c.venue_name     || "",
      venue_address:  c.venue_address  || "",
      venue_maps_url: c.venue_maps_url || "",
      dress_code:     c.dress_code     || "",
      target_date:    c.target_date    || (o.wedding_date || ""),
      love_story:     c.love_story     || "",
      opening_text:   c.opening_text   || "",
      music_url:      c.music_url      || "",
      live_streaming_url: c.live_streaming_url || "",
      gallery:        Array.isArray(c.gallery) ? c.gallery : [],
    });

    // Load daftar tamu jika wedding ada & addon link_personal aktif
    if (w && o.addons?.includes("link_personal")) {
      setGuestsLoading(true);
      const r = await fetch(`${SUPABASE_URL}/rest/v1/guests?wedding_id=eq.${w.id}&order=created_at.asc&limit=200`, {
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
      });
      const guests = r.ok ? await r.json() : [];
      setGuestList(Array.isArray(guests) ? guests : []);
      setGuestsLoading(false);
    }
  }

  // ── Confirm payment + activate ──
  async function confirmAndActivate() {
    if (!selected || saving) return;
    setSaving(true);
    const slug = editSlug.trim() || slugify(selected.groom_name, selected.bride_name);

    // Bersihkan content dari field kosong, tapi pertahankan gallery array
    const { gallery, ...textFields } = editContent;
    const contentPayload = {
      ...Object.fromEntries(Object.entries(textFields).filter(([,v]) => v !== "")),
      ...(gallery.length > 0 ? { gallery } : {}),
    };

    // 1. Buat atau update wedding record
    const wList = await sbGet(`weddings?order_number=eq.${selected.order_number}&limit=1`);
    const wExists = Array.isArray(wList) && wList.length > 0;

    let weddingOk = false;
    if (!wExists) {
      const result = await sbPostWithError("weddings", {
        order_number: selected.order_number,
        slug,
        theme:        selected.theme || "eternal",
        status:       "active",
        groom_name:   selected.groom_name,
        bride_name:   selected.bride_name,
        wedding_date: selected.wedding_date || null,
        expires_at:   selected.addons?.includes("selamanya")
          ? null
          : new Date(Date.now() + 365*24*60*60*1000).toISOString(),
        addons:       selected.addons || [],
        content:      contentPayload,
      });
      if (!result.ok) {
        setSaving(false);
        showToast(`⚠ ${result.error}`);
        return;
      }
      weddingOk = true;
    } else {
      const patched = await sbPatch(`weddings?order_number=eq.${selected.order_number}`, {
        slug, status: "active", content: contentPayload,
      });
      weddingOk = !!patched;
      if (!weddingOk) {
        setSaving(false);
        showToast("⚠ Gagal update wedding record");
        return;
      }
    }

    // 2. Update order status + slug (hanya jika wedding berhasil)
    const updated = await sbPatch(
      `orders?order_number=eq.${selected.order_number}`,
      { status:"active", slug, payment_confirmed_at: new Date().toISOString() }
    );

    setSaving(false);
    if (updated) {
      setOrders(prev => prev.map(o => o.order_number===selected.order_number ? {...o,...updated} : o));
      setSelected({...selected,...updated});
      showToast("✦ Undangan berhasil diaktifkan!");
    } else {
      showToast("⚠ Wedding dibuat tapi order gagal diupdate");
    }
  }

  // ── Update status saja ──
  async function updateStatus() {
    if (!selected || saving) return;
    setSaving(true);
    const updated = await sbPatch(
      `orders?order_number=eq.${selected.order_number}`,
      { status: editStatus }
    );
    setSaving(false);
    if (updated) {
      setOrders(prev=>prev.map(o=>o.order_number===selected.order_number?{...o,...updated}:o));
      setSelected({...selected,...updated});
      showToast("Status diperbarui");
    }
  }

  // ── Simpan konten undangan saja ──
  async function saveContent() {
    if (!selected || contentSaving) return;
    setContentSaving(true);
    const { gallery, ...textFields } = editContent;
    const contentPayload = {
      ...Object.fromEntries(Object.entries(textFields).filter(([,v]) => v !== "")),
      ...(gallery.length > 0 ? { gallery } : {}),
    };
    // Pastikan wedding record sudah ada sebelum patch
    const wList = await sbGet(`weddings?order_number=eq.${selected.order_number}&limit=1`);
    const wExists = Array.isArray(wList) && wList.length > 0;
    let ok;
    if (wExists) {
      ok = await sbPatch(`weddings?order_number=eq.${selected.order_number}`, {
        content: contentPayload,
      });
    } else {
      // wedding belum ada, buat dulu
      ok = await sbPost("weddings", {
        order_number: selected.order_number,
        slug:         editSlug.trim() || slugify(selected.groom_name, selected.bride_name),
        theme:        selected.theme || "eternal",
        status:       selected.status === "active" ? "active" : "in_progress",
        groom_name:   selected.groom_name,
        bride_name:   selected.bride_name,
        wedding_date: selected.wedding_date || null,
        expires_at:   selected.addons?.includes("selamanya")
          ? null
          : new Date(Date.now() + 365*24*60*60*1000).toISOString(),
        addons:       selected.addons || [],
        content:      contentPayload,
      });
    }
    setContentSaving(false);
    if (ok) showToast("✦ Konten disimpan");
    else showToast("⚠ Gagal menyimpan konten");
  }

  // ── Filtered list ──
  const FILTER_OPTS = [
    { value:"all",               label:"Semua" },
    { value:"payment_uploaded",  label:"Bukti Masuk" },
    { value:"payment_confirmed", label:"Dikonfirmasi" },
    { value:"in_progress",       label:"Dikerjakan" },
    { value:"active",            label:"Aktif" },
    { value:"pending_payment",   label:"Pending" },
  ];

  const filtered = orders.filter(o => {
    const matchFilter = filter==="all" || o.status===filter;
    const q = search.toLowerCase();
    const matchSearch = !q ||
      o.order_number?.toLowerCase().includes(q) ||
      o.groom_name?.toLowerCase().includes(q) ||
      o.bride_name?.toLowerCase().includes(q) ||
      o.contact_phone?.includes(q);
    return matchFilter && matchSearch;
  });

  // ── Stats ──
  const stats = {
    total:    orders.length,
    pending:  orders.filter(o=>o.status==="payment_uploaded").length,
    active:   orders.filter(o=>o.status==="active").length,
    revenue:  orders.filter(o=>["payment_confirmed","in_progress","active"].includes(o.status))
                    .reduce((s,o)=>s+(o.total_price||0),0),
  };

  // ── WA link ──
  const waLink = selected?.status==="active" && selected?.slug
    ? buildWaLink(selected)
    : null;

  // ════════════════════════════════════════════════════════════
  // RENDER: LOGIN
  // ════════════════════════════════════════════════════════════
  if (!authed) return (
    <div style={{background:"#0A0907",minHeight:"100vh"}}>
      <style>{CSS}</style>
      <div className="ad-login">
        <div className="ad-login-box">
          <div className="ad-login-title">✦ Admin</div>
          <p className="ad-login-sub">MyKisah Dashboard</p>
          <input className="ad-login-inp" type="password"
            placeholder="••••••••" value={pass}
            onChange={e=>setPass(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&login()}/>
          <button className="ad-login-btn" onClick={login}>Masuk</button>
          {passErr && <p className="ad-login-err">{passErr}</p>}
        </div>
      </div>
    </div>
  );

  // ════════════════════════════════════════════════════════════
  // RENDER: DASHBOARD
  // ════════════════════════════════════════════════════════════
  return (
    <div style={{background:"#0A0907",minHeight:"100vh"}}>
      <style>{CSS}</style>

      <div className="ad-layout">

        {/* ── SIDEBAR ── */}
        <nav className="ad-nav">
          <div className="ad-nav-logo">
            ✦ MYKISAH
            <span>Admin Panel</span>
          </div>
          <div className="ad-nav-section">MENU</div>
          {[
            { id:"orders",    icon:"📋", label:"Orders",
              badge: stats.pending > 0 ? String(stats.pending) : null },
            { id:"stats",     icon:"📊", label:"Statistik" },
          ].map(item => (
            <div key={item.id}
              className={`ad-nav-item${tab===item.id?" active":""}`}
              onClick={()=>setTab(item.id)}>
              <span className="ad-nav-icon">{item.icon}</span>
              {item.label}
              {item.badge && <span className="ad-nav-badge">{item.badge}</span>}
            </div>
          ))}
          <div style={{flex:1}}/>
          <div className="ad-nav-item" style={{color:"rgba(201,123,111,.5)"}}
            onClick={()=>{sessionStorage.removeItem("mk_admin");setAuthed(false);}}>
            <span className="ad-nav-icon">🚪</span> Keluar
          </div>
        </nav>

        {/* ── MAIN ── */}
        <div className="ad-main">
          <div className="ad-topbar">
            <div className="ad-topbar-title">
              {tab==="orders" ? "Semua Order" : "Statistik"}
            </div>
            <div className="ad-topbar-right">
              <button className="ad-refresh" onClick={fetchOrders}>
                {loading ? <span className="ad-spin"/> : "↻ Refresh"}
              </button>
            </div>
          </div>

          <div className="ad-content">

            {/* ── STATS TAB ── */}
            {tab==="stats" && (
              <>
                <div className="ad-stats">
                  {[
                    ["TOTAL ORDER",   stats.total,              "semua waktu"],
                    ["BUKTI MASUK",   stats.pending,            "perlu dikonfirmasi"],
                    ["AKTIF",         stats.active,             "undangan live"],
                    ["PENDAPATAN",    fmt(stats.revenue),       "sudah dikonfirmasi"],
                  ].map(([l,v,s]) => (
                    <div key={l} className="ad-stat">
                      <div className="ad-stat-label">{l}</div>
                      <div className="ad-stat-val">{v}</div>
                      <div className="ad-stat-sub">{s}</div>
                    </div>
                  ))}
                </div>

                {/* Revenue per tema */}
                <div style={{border:"1px solid rgba(201,168,76,.09)",padding:"20px 22px"}}>
                  <div style={{fontFamily:"'Cinzel',serif",fontSize:".42rem",letterSpacing:".38em",color:"rgba(201,168,76,.44)",marginBottom:16}}>TEMA TERPOPULER</div>
                  {Object.entries(
                    orders.reduce((acc,o) => {
                      if (!o.theme) return acc;
                      acc[o.theme] = (acc[o.theme]||0)+1;
                      return acc;
                    }, {})
                  ).sort((a,b)=>b[1]-a[1]).map(([theme,count]) => (
                    <div key={theme} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid rgba(255,255,255,.04)",fontSize:".45rem",letterSpacing:".1em"}}>
                      <span style={{color:"rgba(240,232,216,.65)",textTransform:"capitalize"}}>{theme}</span>
                      <span style={{color:"rgba(201,168,76,.7)",fontFamily:"'Cinzel',serif"}}>{count} order</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ── ORDERS TAB ── */}
            {tab==="orders" && (
              <>
                <div className="ad-stats">
                  {[
                    ["TOTAL",       stats.total,         "order"],
                    ["BUKTI MASUK", stats.pending,        "perlu aksi"],
                    ["AKTIF",       stats.active,         "undangan live"],
                    ["PENDAPATAN",  fmt(stats.revenue),   "terkonfirmasi"],
                  ].map(([l,v,s]) => (
                    <div key={l} className="ad-stat">
                      <div className="ad-stat-label">{l}</div>
                      <div className="ad-stat-val">{v}</div>
                      <div className="ad-stat-sub">{s}</div>
                    </div>
                  ))}
                </div>

                {/* Filters */}
                <div className="ad-filters">
                  {FILTER_OPTS.map(f => (
                    <button key={f.value}
                      className={`ad-filter${filter===f.value?" active":""}`}
                      onClick={()=>setFilter(f.value)}>
                      {f.label}
                      {f.value!=="all" &&
                        <span style={{marginLeft:5,opacity:.6}}>
                          ({orders.filter(o=>o.status===f.value).length})
                        </span>
                      }
                    </button>
                  ))}
                  <input className="ad-search-inp"
                    placeholder="Cari nama / order / WA..."
                    value={search} onChange={e=>setSearch(e.target.value)}/>
                </div>

                {/* Table */}
                <div className="ad-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>ORDER</th>
                        <th>NAMA</th>
                        <th>TEMA</th>
                        <th>TOTAL</th>
                        <th>STATUS</th>
                        <th>MASUK</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length===0 && (
                        <tr><td colSpan={6} className="ad-empty">Tidak ada order</td></tr>
                      )}
                      {filtered.map(o => {
                        const m = statusMeta(o.status);
                        return (
                          <tr key={o.order_number} onClick={()=>openOrder(o)}>
                            <td className="td-order">{o.order_number}</td>
                            <td className="td-couple">
                              {o.groom_name} <span style={{opacity:.3}}>&</span>{" "}
                              <span className="td-bride">{o.bride_name}</span>
                            </td>
                            <td style={{textTransform:"capitalize",color:"rgba(240,232,216,.5)"}}>
                              {o.theme}
                            </td>
                            <td className="td-price">{fmt(o.total_price)}</td>
                            <td>
                              <span className="td-badge"
                                style={{color:m.color,borderColor:m.color+"44",background:m.color+"0d"}}>
                                {m.label}
                              </span>
                            </td>
                            <td className="td-time">{timeAgo(o.created_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

          </div>
        </div>{/* /ad-main */}
      </div>{/* /ad-layout */}

      {/* ── ORDER DRAWER ── */}
      {selected && (
        <>
          <div className="ad-overlay" onClick={()=>setSelected(null)}/>
          <div className="ad-drawer">

            <div className="ad-drawer-head">
              <div>
                <div style={{fontFamily:"'Cinzel',serif",fontSize:".44rem",letterSpacing:".26em",color:"rgba(201,168,76,.55)",marginBottom:5}}>
                  {selected.order_number}
                </div>
                <div className="ad-drawer-title">
                  {selected.groom_name} &amp; {selected.bride_name}
                </div>
              </div>
              <button className="ad-drawer-close" onClick={()=>setSelected(null)}>✕</button>
            </div>

            <div className="ad-drawer-body">

              {/* Detail */}
              <div className="ad-drawer-section">
                <div className="ad-drawer-sec-title">DETAIL ORDER</div>
                <div className="ad-drow">
                  {[
                    ["Tema",    selected.theme],
                    ["Bundle",  selected.bundle ? selected.bundle.charAt(0).toUpperCase()+selected.bundle.slice(1) : "À la carte"],
                    ["Total",   fmt(selected.total_price)],
                    ["WA",      selected.contact_phone],
                    ["Email",   selected.contact_email||"—"],
                    ["Tgl Nikah", selected.wedding_date
                      ? new Date(selected.wedding_date).toLocaleDateString("id-ID",{day:"numeric",month:"long",year:"numeric"})
                      : "—"],
                  ].map(([k,v])=>(
                    <div key={k} className="ad-drow-item">
                      <div className="ad-drow-key">{k}</div>
                      <div className="ad-drow-val">{v}</div>
                    </div>
                  ))}
                  {selected.notes && (
                    <div className="ad-drow-item" style={{gridColumn:"span 2"}}>
                      <div className="ad-drow-key">Catatan</div>
                      <div className="ad-drow-val" style={{color:"rgba(201,168,76,.65)"}}>{selected.notes}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Add-ons */}
              {selected.addons?.length > 0 && (
                <div className="ad-drawer-section">
                  <div className="ad-drawer-sec-title">ADD-ON ({selected.addons.length})</div>
                  <div className="ad-chips">
                    {selected.addons.map(id=>(
                      <span key={id} className="ad-chip">{ADDON_LABELS[id]||id}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Bukti bayar */}
              {selected.payment_proof_url && (
                <div className="ad-drawer-section">
                  <div className="ad-drawer-sec-title">BUKTI TRANSFER</div>
                  <a href={selected.payment_proof_url} target="_blank" rel="noopener">
                    <img src={selected.payment_proof_url} alt="bukti" className="ad-proof-img"/>
                  </a>
                </div>
              )}

              {/* Update status */}
              <div className="ad-drawer-section">
                <div className="ad-drawer-sec-title">UPDATE STATUS</div>
                <select className="ad-status-sel"
                  value={editStatus} onChange={e=>setEditStatus(e.target.value)}>
                  {STATUS_OPTS.map(s=>(
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              {/* Content Editor undangan */}
              <div className="ad-drawer-section">
                <div className="ad-drawer-sec-title">KONTEN UNDANGAN
                  <span style={{marginLeft:8,fontSize:".34rem",letterSpacing:".08em",
                    color:"rgba(122,158,126,.6)",fontFamily:"sans-serif",fontWeight:300,
                    textTransform:"none"}}>← auto dari form order user</span>
                </div>
                <div className="ad-ce-grid">
                  <div className="ad-ce-field">
                    <div className="ad-ce-label">TANGGAL AKAD</div>
                    <input className="ad-ce-inp" placeholder="mis. Sabtu, 12 Juli 2025"
                      value={editContent.akad_date}
                      onChange={e=>setEditContent(p=>({...p,akad_date:e.target.value}))}/>
                  </div>
                  <div className="ad-ce-field">
                    <div className="ad-ce-label">JAM AKAD</div>
                    <input className="ad-ce-inp" placeholder="mis. 08.00 WIB"
                      value={editContent.akad_time}
                      onChange={e=>setEditContent(p=>({...p,akad_time:e.target.value}))}/>
                  </div>
                  <div className="ad-ce-field">
                    <div className="ad-ce-label">TANGGAL RESEPSI</div>
                    <input className="ad-ce-inp" placeholder="mis. Sabtu, 12 Juli 2025"
                      value={editContent.resepsi_date}
                      onChange={e=>setEditContent(p=>({...p,resepsi_date:e.target.value}))}/>
                  </div>
                  <div className="ad-ce-field">
                    <div className="ad-ce-label">JAM RESEPSI</div>
                    <input className="ad-ce-inp" placeholder="mis. 10.00 – 14.00 WIB"
                      value={editContent.resepsi_time}
                      onChange={e=>setEditContent(p=>({...p,resepsi_time:e.target.value}))}/>
                  </div>
                  <div className="ad-ce-field ad-ce-full">
                    <div className="ad-ce-label">NAMA VENUE</div>
                    <input className="ad-ce-inp" placeholder="mis. Gedung Serbaguna Al-Ikhlas"
                      value={editContent.venue_name}
                      onChange={e=>setEditContent(p=>({...p,venue_name:e.target.value}))}/>
                  </div>
                  <div className="ad-ce-field ad-ce-full">
                    <div className="ad-ce-label">ALAMAT VENUE</div>
                    <input className="ad-ce-inp" placeholder="mis. Jl. Merdeka No. 12, Purbalingga"
                      value={editContent.venue_address}
                      onChange={e=>setEditContent(p=>({...p,venue_address:e.target.value}))}/>
                  </div>
                  <div className="ad-ce-field ad-ce-full">
                    <div className="ad-ce-label">LINK GOOGLE MAPS</div>
                    <input className="ad-ce-inp" placeholder="https://maps.google.com/..."
                      value={editContent.venue_maps_url}
                      onChange={e=>setEditContent(p=>({...p,venue_maps_url:e.target.value}))}/>
                  </div>
                  <div className="ad-ce-field">
                    <div className="ad-ce-label">DRESS CODE</div>
                    <input className="ad-ce-inp" placeholder="mis. Dusty Rose"
                      value={editContent.dress_code}
                      onChange={e=>setEditContent(p=>({...p,dress_code:e.target.value}))}/>
                  </div>
                  <div className="ad-ce-field">
                    <div className="ad-ce-label">TARGET COUNTDOWN (ISO)</div>
                    <input className="ad-ce-inp" placeholder="mis. 2025-07-12T08:00:00"
                      value={editContent.target_date}
                      onChange={e=>setEditContent(p=>({...p,target_date:e.target.value}))}/>
                  </div>
                  <div className="ad-ce-field ad-ce-full">
                    <div className="ad-ce-label">LOVE STORY (OPSIONAL)</div>
                    <textarea className="ad-ce-inp ad-ce-ta"
                      placeholder="Cerita singkat perjalanan cinta pengantin..."
                      value={editContent.love_story}
                      onChange={e=>setEditContent(p=>({...p,love_story:e.target.value}))}/>
                  </div>
                  <div className="ad-ce-field ad-ce-full">
                    <div className="ad-ce-label">KATA PEMBUKA (OPSIONAL)</div>
                    <textarea className="ad-ce-inp ad-ce-ta" rows={3}
                      placeholder="mis. Bismillahirrahmanirrahim. Dengan memohon rahmat dan ridho Allah SWT..."
                      value={editContent.opening_text}
                      onChange={e=>setEditContent(p=>({...p,opening_text:e.target.value}))}/>
                  </div>
                  <div className="ad-ce-field ad-ce-full">
                    <div className="ad-ce-label">🎵 LINK MUSIK LATAR (OPSIONAL)</div>
                    <input className="ad-ce-inp"
                      placeholder="Link file .mp3 / Google Drive / Dropbox"
                      value={editContent.music_url}
                      onChange={e=>setEditContent(p=>({...p,music_url:e.target.value}))}/>
                  </div>
                  <div className="ad-ce-field ad-ce-full">
                    <div className="ad-ce-label">📺 LINK LIVE STREAMING (OPSIONAL)</div>
                    <input className="ad-ce-inp"
                      placeholder="mis. https://youtube.com/live/xxxxx"
                      value={editContent.live_streaming_url}
                      onChange={e=>setEditContent(p=>({...p,live_streaming_url:e.target.value}))}/>
                  </div>
                </div>
                <button className="ad-btn-ghost" style={{marginTop:10,width:"100%"}}
                  onClick={saveContent} disabled={contentSaving}>
                  {contentSaving ? <span className="ad-spin"/> : "💾 Simpan Konten"}
                </button>
              </div>

              {/* Preview Foto Galeri dari order */}
              {editContent.gallery.length > 0 && (
                <div className="ad-drawer-section">
                  <div className="ad-drawer-sec-title">
                    FOTO GALERI
                    <span style={{marginLeft:8,fontSize:".34rem",letterSpacing:".08em",color:"rgba(122,158,126,.6)",fontFamily:"sans-serif",fontWeight:300,textTransform:"none"}}>
                      {editContent.gallery.length} foto dari order
                    </span>
                  </div>
                  <div className="ad-gal-grid">
                    {Array.from({length:8},(_,i)=>(
                      editContent.gallery[i]
                        ? <div key={i} className="ad-gal-thumb"><img src={editContent.gallery[i]} alt=""/></div>
                        : <div key={i} className="ad-gal-empty">—</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Daftar Tamu (add-on link_personal) */}
              {selected.addons?.includes("link_personal") && selected.status === "active" && (
                <div className="ad-drawer-section">
                  <div className="ad-drawer-sec-title">
                    DAFTAR TAMU
                    <span style={{marginLeft:8,fontSize:".34rem",letterSpacing:".08em",color:"rgba(201,168,76,.5)",fontFamily:"sans-serif",fontWeight:300,textTransform:"none"}}>
                      {guestsLoading ? "memuat…" : `${guestList.length} tamu`}
                    </span>
                  </div>
                  {guestsLoading
                    ? <div style={{textAlign:"center",padding:14}}><span className="ad-spin"/></div>
                    : guestList.length === 0
                      ? <div className="ad-guest-empty">Belum ada tamu. User menambah sendiri via cek-order.</div>
                      : (
                        <div style={{border:"1px solid rgba(201,168,76,.08)",maxHeight:220,overflowY:"auto"}}>
                          {guestList.map(g => (
                            <div key={g.id} className="ad-guest-row">
                              <div className="ad-guest-name">{g.name}</div>
                              {g.phone && <div className="ad-guest-phone">{g.phone}</div>}
                              <div className="ad-guest-token">{g.token}</div>
                            </div>
                          ))}
                        </div>
                      )
                  }
                </div>
              )}

              {/* Slug undangan */}
              <div className="ad-drawer-section">
                <div className="ad-drawer-sec-title">SLUG UNDANGAN</div>
                <div className="ad-slug-row">
                  <div className="ad-slug-prefix">/undangan/</div>
                  <input className="ad-slug-inp"
                    value={editSlug} onChange={e=>setEditSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,""))}
                    placeholder="nama-pengantin"/>
                </div>
                {editSlug && (
                  <div style={{marginTop:6,fontSize:".38rem",letterSpacing:".1em",color:"rgba(240,232,216,.25)"}}>
                    {SITE_URL}/undangan/{editSlug}
                  </div>
                )}
              </div>

              {/* Link undangan aktif + status wedding record */}
              {selected.slug && (
                <div className="ad-drawer-section">
                  <div className="ad-drawer-sec-title">LINK UNDANGAN</div>
                  <div style={{fontSize:".45rem",letterSpacing:".1em",padding:"10px 12px",border:"1px solid rgba(201,168,76,.18)",color:"rgba(201,168,76,.7)",wordBreak:"break-all",marginBottom:8}}>
                    {SITE_URL}/undangan/{selected.slug}
                  </div>
                  {selected.status === "active" && (
                    <a href={`${SITE_URL}/undangan/${selected.slug}`} target="_blank" rel="noopener"
                      style={{fontSize:".42rem",letterSpacing:".14em",color:"rgba(201,168,76,.55)",textDecoration:"none",display:"inline-flex",alignItems:"center",gap:6}}>
                      🔗 Buka undangan →
                    </a>
                  )}
                </div>
              )}

            </div>{/* /drawer-body */}

            {/* ── ACTIONS ── */}
            <div className="ad-drawer-actions">

              {/* Kirim link via WA — hanya saat active */}
              {waLink && (
                <a href={waLink} target="_blank" rel="noopener" style={{textDecoration:"none"}}>
                  <button className="ad-btn-wa" style={{width:"100%"}}>
                    💬 Kirim Link ke Customer
                  </button>
                </a>
              )}

              {/* Konfirmasi bayar + aktifkan — selalu tampil agar bisa re-trigger */}
              <button className="ad-btn-primary" onClick={confirmAndActivate}
                disabled={saving||!editSlug.trim()}>
                {saving ? <span className="ad-spin"/> : (
                  selected.status === "active"
                    ? "↻ Sync Wedding Record"
                    : "✦ Konfirmasi & Aktifkan"
                )}
              </button>

              {/* Update status saja */}
              <div className="ad-act-row">
                <button className="ad-btn-ghost" onClick={updateStatus} disabled={saving}>
                  Simpan Status
                </button>
                <button className="ad-btn-danger" onClick={()=>setSelected(null)}>
                  Tutup
                </button>
              </div>

            </div>
          </div>
        </>
      )}

      {/* ── TOAST ── */}
      {toast && <div className="ad-toast">{toast}</div>}

    </div>
  );
}
